import type { Scenario } from '../types'
import { ROMANCE_SCENARIOS } from './scenariosRomance'
import { MORE_SCENARIOS } from './scenariosMore'
import { NUMBER_SCENARIOS } from './scenariosNumbers'

/**
 * Scenarios for the Latin-script languages.
 *
 * Keyed by language so the next one is data, not code. Every beat points at a
 * block that exists in that language's starter islands — seed.test.ts enforces
 * it, because a beat aimed at a missing block is silently dropped at runtime
 * and the scenario just gets shorter with no explanation.
 *
 * `promptPinyin` stays empty: these languages have no phonetic line. See
 * lib/languages.ts for why the field is still called that.
 */

const SEED_CLOCK = 0
const freshStats = () => ({ runs: 0, bestScore: 0, lastScore: null, lastRunAt: null })

export interface SeedBeat {
  speaker: string
  prompt: string
  english: string
  cue: string
  island: string
  blocks: string[]
  hint?: string
}

export interface SeedScenario {
  slug: string
  title: string
  nativeTitle: string
  setting: string
  brief: string
  beats: SeedBeat[]
}

const CONTENT: Record<string, SeedScenario[]> = {
  ...ROMANCE_SCENARIOS,
  de: [
    {
      slug: 'party',
      title: 'Meeting someone at a party',
      nativeTitle: 'Auf einer Party',
      setting: 'Berlin, eine Wohnungsparty am Freitagabend',
      brief:
        'A flat party in Berlin. Someone turns from the kitchen counter and starts talking. Three of your islands cover everything he is about to ask — the only question is whether you can reach the right one before the pause gets long.',
      beats: [
        {
          speaker: 'Lukas',
          prompt: 'Hallo, ich bin Lukas. Und was machst du beruflich?',
          english: "Hi, I'm Lukas. What do you do for a living?",
          cue: 'Say what you do and who for.',
          island: 'de_arbeit',
          blocks: ['de_arbeit_b1', 'de_arbeit_b2'],
          hint: 'Job first, then the company. Watch the verb staying in second place.',
        },
        {
          speaker: 'Lukas',
          prompt: 'Klingt spannend. Dein Deutsch ist echt gut — wie lange lernst du schon?',
          english: 'Sounds interesting. Your German is really good — how long have you been learning?',
          cue: 'Give the length of time, then why you started.',
          island: 'de_warum',
          blocks: ['de_warum_b1', 'de_warum_b2'],
          hint: 'The compliment is the trigger. This island should be automatic.',
        },
        {
          speaker: 'Lukas',
          prompt: 'Bist du allein hier, oder hast du Familie dabei?',
          english: 'Are you here alone, or do you have family with you?',
          cue: 'Account for your household.',
          island: 'de_familie',
          blocks: ['de_familie_b1', 'de_familie_b2'],
        },
        {
          speaker: 'Lukas',
          prompt: 'Und arbeitest du im Büro oder von zu Hause?',
          english: 'And do you work in the office or from home?',
          cue: 'Say where you usually work.',
          island: 'de_arbeit',
          blocks: ['de_arbeit_b4'],
          hint: 'Normalerweise first — then the verb, then you.',
        },
      ],
    },
    {
      slug: 'restaurant',
      title: 'Dinner for two',
      nativeTitle: 'Im Restaurant',
      setting: 'Ein volles Lokal, Samstagabend, halb acht',
      brief:
        'A busy restaurant at half past seven. The waiter is moving fast and will not slow down for you. Every beat is a sentence you already have — this is a speed test, not a comprehension one.',
      beats: [
        {
          speaker: 'Kellner',
          prompt: 'Guten Abend! Für wie viele Personen?',
          english: 'Good evening! For how many people?',
          cue: 'Answer in one breath.',
          island: 'de_restaurant',
          blocks: ['de_restaurant_b1'],
        },
        {
          speaker: 'Kellner',
          prompt: 'Bitte, hier entlang. Ich komme gleich wieder.',
          english: 'This way, please. I will be right back.',
          cue: 'Stop him before he goes — you want the menu.',
          island: 'de_restaurant',
          blocks: ['de_restaurant_b2'],
          hint: 'Könnten wir… is the polite opener that buys you the half-second.',
        },
        {
          speaker: 'Kellner',
          prompt: 'So, was darf ich Ihnen bringen?',
          english: 'Right, what can I bring you?',
          cue: 'Order a dish.',
          island: 'de_restaurant',
          blocks: ['de_restaurant_b3'],
        },
        {
          speaker: 'Kellner',
          prompt: 'Gerne. Möchten Sie das mit Zwiebeln?',
          english: 'Certainly. Would you like that with onions?',
          cue: 'Ask for it without, politely.',
          island: 'de_restaurant',
          blocks: ['de_restaurant_b4'],
        },
        {
          speaker: 'Kellner',
          prompt: 'Sonst noch etwas?',
          english: 'Anything else?',
          cue: 'Close it out — you want to pay.',
          island: 'de_restaurant',
          blocks: ['de_restaurant_b5'],
        },
      ],
    },
    {
      slug: 'zug',
      title: 'Small talk on the train',
      nativeTitle: 'Im Zug nach München',
      setting: 'ICE nach München, vier Stunden, der Platz gegenüber wird frei',
      brief:
        'Four hours to Munich and the woman opposite has decided to talk. Nothing here is transactional — she is just curious, which means no script fits perfectly and you have to pick the closest island fast.',
      beats: [
        {
          speaker: 'Frau Weber',
          prompt: 'Entschuldigung — Sie lernen Deutsch, oder? Warum denn?',
          english: "Excuse me — you're learning German, aren't you? Why, then?",
          cue: 'Say how it started.',
          island: 'de_warum',
          blocks: ['de_warum_b2'],
        },
        {
          speaker: 'Frau Weber',
          prompt: 'Und was führt Sie nach München — beruflich?',
          english: 'And what brings you to Munich — work?',
          cue: 'Say what you do.',
          island: 'de_arbeit',
          blocks: ['de_arbeit_b1', 'de_arbeit_b2'],
        },
        {
          speaker: 'Frau Weber',
          prompt: 'Haben Sie Familie hier in Deutschland?',
          english: 'Do you have family here in Germany?',
          cue: 'Account for your household.',
          island: 'de_familie',
          blocks: ['de_familie_b1', 'de_familie_b2'],
        },
        {
          speaker: 'Frau Weber',
          prompt: 'Was finden Sie denn am schwierigsten an der Sprache?',
          english: 'What do you find hardest about the language?',
          cue: 'Name the hard part.',
          island: 'de_warum',
          blocks: ['de_warum_b3'],
          hint: 'Am schwierigsten finde ich… — superlative first, verb second.',
        },
        {
          speaker: 'Frau Weber',
          prompt: 'Und üben Sie jeden Tag?',
          english: 'And do you practise every day?',
          cue: 'Say when and how much.',
          island: 'de_warum',
          blocks: ['de_warum_b4'],
        },
      ],
    },
  ],
}

/** Scenarios for one language. Empty for anything not listed above. */
export function buildLatinScenarios(language: string): Scenario[] {
  const all = [
    ...(CONTENT[language] ?? []),
    ...(MORE_SCENARIOS[language] ?? []),
    ...(NUMBER_SCENARIOS[language] ?? []),
  ]
  return all.map((seed) => ({
    id: `scn_${language}_${seed.slug}`,
    language,
    title: seed.title,
    titleZh: seed.nativeTitle,
    setting: seed.setting,
    brief: seed.brief,
    isStarter: true,
    updatedAt: SEED_CLOCK,
    stats: freshStats(),
    beats: seed.beats.map((beat, i) => ({
      id: `scn_${language}_${seed.slug}_b${i + 1}`,
      speaker: beat.speaker,
      promptHanzi: beat.prompt,
      promptPinyin: '',
      promptEnglish: beat.english,
      cue: beat.cue,
      expectIslandId: beat.island,
      expectBlockIds: beat.blocks,
      ...(beat.hint ? { hint: beat.hint } : {}),
    })),
  }))
}

export const SCENARIO_LANGUAGES = Object.keys(CONTENT)
